import { ChangeDetectionStrategy, Component, computed, forwardRef, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';

import { FeedPage } from '../shared/feed-page';
import { FeedPageHeaderComponent } from '../shared/feed-page-header.component';
import { FeedBasicsCardComponent } from '../shared/feed-basics-card.component';
import { FeedIdentityCardComponent } from '../shared/feed-identity-card.component';
import { FeedFilesComponent } from '../shared/feed-files.component';
import { FeedRawDiagnosticsComponent } from '../shared/feed-raw-diagnostics.component';
import { ConfigSchemaFormComponent } from '../shared/config-schema-form.component';
import { configProblems } from '../util/config-schema.util';
import { TextInput } from '../../../shared/components/forms/text-input';

/**
 * Feed page for provider types without a dedicated UI
 * (/admin/providers/:providerId/:feedId, 'new' = create). The configuration is edited with a
 * form built from the provider's descriptor (`ConfigSchemaFormComponent`); a provider without a
 * descriptor (never discovered) falls back to free-form key/value rows passed on as-is. Plus the
 * raw latest diagnostics snapshot and, when the provider accepts files, the Files card.
 */
@Component({
  selector: 'app-generic-feed-page',
  standalone: true,
  imports: [
    FormsModule,
    TranslocoDirective,
    TextInput,
    ConfigSchemaFormComponent,
    FeedPageHeaderComponent,
    FeedBasicsCardComponent,
    FeedIdentityCardComponent,
    FeedFilesComponent,
    FeedRawDiagnosticsComponent,
  ],
  template: `
    <ng-container *transloco="let t; read: 'providers.feedPage'">
      <div class="flex h-full min-h-0 flex-col">
        <app-feed-page-header />
        <div class="min-h-0 flex-1 overflow-auto px-8 pb-10 pt-6">
          <div class="mx-auto grid max-w-[1180px] items-start gap-6" style="grid-template-columns: minmax(0, 1fr) 380px">
            <div class="flex flex-col gap-6">
              <app-feed-basics-card />

              @if (schema(); as s) {
                <!-- Configuration: built from the provider's descriptor -->
                <app-config-schema-form
                  [schema]="s"
                  [providerType]="provider()!.type"
                  [(values)]="configValues"
                  [serverErrors]="configViolations()"
                  (fieldEdited)="dropViolation($event)"
                />
              } @else {
              <!-- Configuration: free-form key/value (provider without a descriptor) -->
              <div class="rounded-card bg-card p-6 shadow-hairline">
                <div class="mb-1 font-display text-[12px] font-semibold uppercase tracking-wide text-ink-2">{{ t('cards.configuration') }}</div>
                <p class="m-0 mb-5 text-[12px] leading-[18px] text-ink-3">{{ t('fields.configurationHelp') }}</p>
                <div class="flex flex-col gap-2">
                  @for (row of configRows; track $index; let i = $index) {
                    <div class="grid grid-cols-[200px_minmax(0,1fr)_24px] items-center gap-3">
                      <app-text-input size="sm" [(ngModel)]="row.key" [ngModelOptions]="{ standalone: true }" [placeholder]="t('fields.configurationKey')"></app-text-input>
                      <app-text-input size="sm" [(ngModel)]="row.value" [ngModelOptions]="{ standalone: true }" [placeholder]="t('fields.configurationValue')"></app-text-input>
                      <button type="button" (click)="removeConfigRow(i)" [attr.aria-label]="t('fields.configurationRemove')" class="text-ink-3 hover:text-danger">&times;</button>
                    </div>
                  } @empty {
                    <span class="text-[13px] text-ink-3">{{ t('fields.configurationEmpty') }}</span>
                  }
                  <button type="button" (click)="addConfigRow()" class="mt-1 w-fit text-[13px] font-semibold text-link hover:underline">
                    {{ t('fields.configurationAdd') }}
                  </button>
                  @if (!configurationValid()) {
                    <p class="m-0 text-[12px] text-danger">{{ t('fields.configurationInvalid') }}</p>
                  }
                </div>
              </div>
              }

              @if (feed(); as f) {
                <app-feed-raw-diagnostics [feedId]="f.id" />
                <!-- Files (only for providers that accept them; core 400s otherwise) -->
                @if (provider()?.acceptsFiles) {
                  <app-feed-files
                    [feedId]="f.id"
                    [filesCapability]="provider()?.descriptor?.capabilities?.files ?? null"
                    [providerType]="provider()?.type ?? null"
                    (changed)="refreshFeedSummary()"
                  ></app-feed-files>
                }
              }
            </div>
            <app-feed-identity-card />
          </div>
        </div>
      </div>
    </ng-container>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: FeedPage, useExisting: forwardRef(() => GenericFeedPageComponent) }],
  host: { class: 'block h-full min-h-0' },
})
export class GenericFeedPageComponent extends FeedPage {
  readonly routeType = null;

  /** The provider's feed config schema; null for a provider without a descriptor. */
  readonly schema = computed(() => this.provider()?.descriptor?.feedConfig ?? null);
  /** The configuration being edited with the schema form. */
  readonly configValues = signal<Record<string, string>>({});
  /** Plain ngModel rows for the free-form editor — see FeedPage.isValid. */
  configRows: { key: string; value: string }[] = [];

  dropViolation(key: string): void {
    this.configViolations.update(({ [key]: _, ...rest }) => rest);
  }

  addConfigRow(): void {
    this.configRows = [...this.configRows, { key: '', value: '' }];
  }

  removeConfigRow(index: number): void {
    this.configRows = this.configRows.filter((_, i) => i !== index);
  }

  /** Schema form: nothing core would reject. Free-form rows: blank rows are ignored; a value
   *  without a key, or a repeated key, isn't saveable. */
  configurationValid(): boolean {
    const schema = this.schema();
    if (schema) {
      return Object.keys(configProblems(schema, this.configValues())).length === 0;
    }
    const keys = new Set<string>();
    for (const row of this.configRows) {
      const key = row.key.trim();
      if (!key) {
        if (row.value.trim()) return false;
        continue;
      }
      if (keys.has(key)) return false;
      keys.add(key);
    }
    return true;
  }

  protected buildConfiguration(): Record<string, string> {
    if (this.schema()) {
      return { ...this.configValues() };
    }
    const configuration: Record<string, string> = {};
    for (const row of this.configRows) {
      if (row.key.trim()) configuration[row.key.trim()] = row.value;
    }
    return configuration;
  }

  // Fills both editors: the provider (and so whether there's a schema) may load after the feed.
  protected applyConfiguration(configuration: Record<string, string> | null): void {
    this.configValues.set({ ...(configuration ?? {}) });
    this.configRows = Object.entries(configuration ?? {}).map(([key, value]) => ({ key, value }));
  }
}
